<script setup lang="ts">
	/* //? usage:

<VModalFinal
  modalId="Id-to-trigger-from-everywhere"
  title="The Modal!"
  :closeOnBackgroudClick="false"
  :showButtons="false"
  :closeButton="false"
  :escToClose="false"
  size="xl"
	:slotdata={}
  @confirm="() => confirm()"
>
  HERE CONTENT
</VModalFinal>
*/

	import { ref } from 'vue'
	import { useVfm, VueFinalModal } from 'vue-final-modal'
	import { useI18n } from 'vue-i18n'

	interface Props {
		title?: string
		modalId: string
		closeOnBackgroudClick?: boolean
		showButtons?: boolean
		closeButton?: boolean
		escToClose?: boolean
		size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
		slotdata?: any
		loading?: boolean
	}

	const { t } = useI18n({ useScope: 'global' })
	const vfm = useVfm()

	const props = withDefaults(defineProps<Props>(), {
		closeOnBackgroudClick: true,
		showButtons: true,
		closeButton: true,
		escToClose: true,
		size: 'md',
		slotdata: null,
		loading: false,
	})

	const emit = defineEmits<{
		(e: 'confirm'): void
		(e: 'close'): void
	}>()

	const isModalVisible = ref(false)

	const openModal = () => {
		isModalVisible.value = true
	}
	const handleClose = () => {
		isModalVisible.value = false
		emit('close')
		if (props.modalId) vfm.close(props.modalId)
	}

	const handleConfirm = async () => {
		try {
			await emit('confirm')
			console.log('OK')
			handleClose()
		} catch (e) {
			console.error(e)
		}
	}

	// const beforeOpen = () => {
	// 	console.log('▼🟨🟩🟥beforeOpen', props.modalId)
	// 	// You can add any logic here before the modal opens
	// }

	// const beforeClose = () => {
	//   console.log('▼🟨🟩🟥beforeClose', props.modalId)
	//   // You can add any logic here before the modal closes
	// }
</script>

<template>
	<!-- variant : vue-final-modal -->

	<vue-final-modal
		v-model="isModalVisible"
		:clickToClose="props.closeOnBackgroudClick"
		:modal-id="props.modalId"
		:esc-to-close="props.escToClose"
		overlay-transition="vfm-fade"
		content-transition="vfm-slide-down"
		class="vfm-modal">
		<!-- :before-open="beforeOpen()"
    :before-close="beforeClose()" -->
		<div class="vfm-modal-box flex" :class="`size-${size} ${$attrs.contentClass}`">
			<template v-if="props.title || props.closeButton">
				<div class="header-part">
					<div class="top-row">
						<slot name="header" />
						<template v-if="props.title">
							<h3 class="text-lg font-bold">{{ props.title }}</h3>
						</template>
						<template v-if="props.closeButton">
							<button @click="handleClose" class="btn btn-sm btn-circle btn-ghost">✕</button>
						</template>
					</div>
				</div>
			</template>

			<div class="main-part">
				<slot slotdata />
			</div>

			<template v-if="props.showButtons || $slots.footer">
				<div class="footer-part">
					<template v-if="props.showButtons">
						<div class="modal-action bottom-row">
							<button @click="handleClose" class="btn">{{ t('CANCEL') }}</button>
							<button @click="handleConfirm" class="btn btn-primary">
								<span
									class="loading loading-spinner"
									v-if="props.loading !== null && props.loading"></span>
								{{ t('Confirm') }}
							</button>
						</div>
					</template>
					<template v-else-if="$slots.footer">
						<div class="modal-action bottom-row">
							<slot name="footer" />
						</div>
					</template>
				</div>
			</template>
		</div>
	</vue-final-modal>
</template>

<style >
	/* @reference '@/styles/tailwind.css'; */


</style>
